import { useEffect, useMemo, useRef, useState } from "react";
import { RestAPI } from "@aws-amplify/api-rest";
import { useNavigate } from "react-router";

import DialogComponentWrapper from "../Utils/DialogComponentWrapper";
import DivPortal from "../Utils/DivPortal";

import { icon } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle, faPlus, faTrashAlt, faPaperPlane } from "@fortawesome/free-solid-svg-icons";

import { UserForm } from "../../pages/Personal/Personal";
import { addDataAWS, deleteDataAWS, modifyDataAWS } from "../../utils/DataUtils";
import getBaseLangs from "../../utils/language";
import { toast } from "react-toastify";
import { useColsEffect, useGridRef } from "../../utils/tables";

import "react-toastify/dist/ReactToastify.css";
import "../Form/FormStyles.css";
import PromiseWrapper from "../Utils/PromiseDialog";
import DataTablePrime from "../PrimeComponents/DataTablePrime";
import LoadingDialog from "../Utils/LoadingDialog";
import { Badge } from "primereact/badge";
import TooltipPrime from "../PrimeComponents/TooltipPrime";

const [deleteIcon] = icon(faTrashAlt, { styles: { "color": "tomato" } }).html;

const getUserInfo = async (email, campo_id,users, usersInvited) => {
    const emailLower = email.toLowerCase();
    if(users?.find(u => u.email===emailLower) ) return { alert: "users.userRegistered" };
    if(usersInvited?.find(u => u.email===emailLower)) return { alert: "users.userInvited" };
    const param = `/users/${campo_id}/search/${emailLower}`;
    const answer = await RestAPI.get("apiAdminDropControl", param).catch(e => console.error(e));
    if (answer?.can_invite) return { data: { email:emailLower, tipousuario_id: 4 }, shouldInvite:true, alert: "forms.notAvailable" };
    if (answer?.is_blocked) return { alert: "forms.notAvailable" };
    if (answer?.should_create) return { data: { email:emailLower, tipousuario_id: 4 }, shouldCreate: true };
    if (answer?.email && answer?.nombre) return { data: { email: answer.email, nombre: answer.nombre, tipousuario_id: 4 } };
    return { alert: "forms.toastError" };
};

const UsersTable = ({ dispatch, usersData, tipousuario, farmId, canEdit, t, i18n, acceso_limitado, setInvitations, invitations }) => {
    const navigate = useNavigate();
    const [dialogState, setDialogState] = useState({ visible: false });
    const [isLoading, setIsLoading] = useState(false);
    const updatingRef = useRef(false);
    const formikRef = useRef(false);
    const grid = useRef(null);
    const promiseWrapper = useRef(null);
    const promiseWrapper2 = useRef(null);

    const inviteUser = async (values, campo_id) => {
        setIsLoading(true);
        const param = `/users/${campo_id}/invite`;
        await RestAPI.post("apiAdminDropControl", param, { body:{"email":values.email,"tipousuario_id":values.tipousuario_id, "locale":i18n.language} })
            .then(res => {
                toast.success(t("invitationPage.inviteToast"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
                const newInvitations = [...invitations];
                newInvitations.push(res.invitation);
                setInvitations(newInvitations);
                setDialogState((ds) => ({ ...ds, user_invited: res.invitation,invite_email:null, disabled: false }));
                setIsLoading(false);
            })
            .catch(err => {
                console.error(err);
                toast.error(t("forms.toastError"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
                setIsLoading(false);}
            );
    };
    const { tipousuarioTemplate, names } = useMemo(() => {
        const lngs = getBaseLangs(i18n);
        return {
            tipousuarioTemplate: (_field, { tipousuario: tu }) => tu ? (tu[lngs[0]] || tu[lngs[1]]) : t("users.custom"),
            names: ["ID", t("general.name"), t("personal.email"), t("users.tipousuario")],
        };
    }, [t, i18n]);

    const tipoUsuarioTemplate = (rowData) => {
        const lngs = getBaseLangs(i18n);
        const { tipousuario: tu } = rowData;
        return tu ? (tu[lngs[0]] || tu[lngs[1]]) : t("users.custom");

    };
    const addRecordFallback = useMemo(() => (record) => {
        dispatch({
            type: "update", path: "users", params: (info) => {
                if (!info || !info.data || !info.data.tipousuario) return;
                if (!record) return null; // returning null dispatchs a rerender
                const users = info.data.users;
                const newUsers = users ? [record, ...users] : [record];
                return { data: { ...info.data, users: newUsers } };
            }
        });
    }, []);

    const users = useMemo((() => usersData || []), [usersData]);

    useEffect(() => {
        if (updatingRef && updatingRef?.current?.type==="update" && updatingRef?.current?.data) {
            const index = dataSource.findIndex(e => e.email === updatingRef.current.data.email);
            dataSource[index] = {...dataSource[index],...updatingRef.current.data};
            updatingRef.current= false;
        }
        if (updatingRef && updatingRef?.current?.type==="delete" && updatingRef?.current?.data && updatingRef?.current?.r) {

            const index = dataSource.findIndex(e => e[updatingRef.current.data.key] === updatingRef.current.data.keyValue);
            dataSource.splice(index, 1);
            dispatch({
                type: "update", path: "users", params: (info) => {
                    if (!info || !info.data || !info.data.tipousuario) return;
                    return { data: { ...info.data, users: dataSource } };
                }
            });
        }
    }, [updatingRef.current]);

    const dataSource = useMemo(() => {
        const data = [];
        if (Array.isArray(users) && users.length > 0) {
            users.forEach(us => {
                us.nombre_completo = `${us.nombre} ${us.apellido_paterno || ""} ${us.apellido_materno || ""}`;
                data.push(us);
            });
        }
        return data;
    }, [users, farmId]);

    useColsEffect(grid, names);
    const gridRef = useGridRef(grid, dataSource);

    const templatesDataPointer = useRef({});
    const templateData = templatesDataPointer.current;
    if (templateData.farmId !== farmId) templateData.farmId = farmId;
    if (templateData.navigate !== navigate) templateData.navigate = navigate;

    // eslint-disable-next-line react/display-name
    const { templateRedirectToUser } = useMemo(() => ({ nombre, permiso_id }) => {
        if (!nombre || !permiso_id) return "-";
        return (
            <a className="pointer" onClick={() => {
                const { farmId, navigate } = templatesDataPointer.current;
                navigate({ pathname: "/farms/user", search: `?farm=${farmId}&user=${permiso_id}` },
                    { state: { expanded: ["usuarios",`permiso-${permiso_id}`], updateTree: true } });
            }}
            >{nombre}</a>
        );
    }, []);
    const emailTemplate = (rowData) => {
        return(
            <div className="user-email-subscription">
                <p>{rowData.email}
                    {
                        rowData.supressed_emails?
                            <TooltipPrime id={`subscription_status_${rowData.usuario_id}`} content={t("users.userUnsuscribed")} position="right">
                                <FontAwesomeIcon style={{ color: "orange", marginLeft:"1rem" }} icon={faExclamationTriangle} />
                            </TooltipPrime>
                            :null
                    }
                </p>
            </div>
        );
    };
    if (!Array.isArray(dataSource)) return null;
    return (<>
        {isLoading && <LoadingDialog />}
        <PromiseWrapper
            ref={promiseWrapper}
            yesText={t("general.answerYes")} noText={t("general.answerNo")}
            headerText={t("general.confirm")} contentText={t("users.deleteConfirm")}
        />
        <PromiseWrapper
            ref={promiseWrapper2}
            yesText={t("general.answerYes")} noText={t("general.answerNo")}
            headerText={t("general.confirm")} contentText={t("users.toMany")}
        />

        <DialogComponentWrapper formikRef={formikRef} containerId="users-table-form"
            t={t} dialogState={dialogState} setDialogState={setDialogState}
            searchButton={!dialogState.data || !dialogState.data.email}
            inviteButton={dialogState.invite_email} closeButtonOnly={dialogState.user_invited}
        >
            <UserForm
                innerRef={formikRef}
                canEdit={canEdit}
                isTableDialog
                tipousuario={tipousuario}
                alert={dialogState.alert}
                hidden={!dialogState.visible}
                msg={dialogState.edit ? "forms.fillData" : null}
                data={dialogState.data?.email && dialogState.data}
                edit={dialogState.edit}
                invite_email={dialogState.invite_email}
                user_invited={dialogState.user_invited}
                tableEdit
                updateData={async (values, args) => {
                    if (updatingRef.current) return;
                    updatingRef.current = true;
                    setDialogState((e) => ({ ...e, disabled: true }));

                    try {
                        if(dialogState.invite_email){
                            inviteUser(values,farmId);
                            updatingRef.current = false;
                            return;
                        }
                        if(dialogState.user_invited){
                            setDialogState({ visible: false});
                            return;
                        }
                        if (!dialogState.existing_id && (!dialogState.data || !dialogState.data.email)) {
                            const result = await getUserInfo(values && values.email.toLowerCase(), farmId, dataSource, invitations);
                            updatingRef.current = false;
                            if (result) {
                                const { data, alert, shouldCreate, shouldInvite } = result;
                                if(shouldInvite){
                                    setDialogState((ds) => ({ ...ds, data, invite_email: {email:values.email}, disabled: false }));
                                }else{
                                    setDialogState((ds) => ({ ...ds, data, edit: shouldCreate, alert, disabled: false }));
                                }
                            } else {
                                setDialogState((e) => ({ ...e, disabled: false }));
                            }
                            return;
                        }
                        if (dialogState.existing_id) {
                            modifyDataAWS(values, dialogState.data, setDialogState, dialogState.existing_id,
                                `/users/${farmId}/permission/${dialogState.existing_id}`, t, updatingRef, "email",dataSource
                            );
                        } else {
                            values.existing = !dialogState.edit;
                            values.locale = i18n && i18n.language;
                            addDataAWS(values, setDialogState, `/users/${farmId}/permission/new`, t, updatingRef, addRecordFallback);
                        }
                    } catch (e) {
                        updatingRef.current = false;
                        setDialogState((e) => ({ ...e, disabled: false }));
                        throw e;
                    }
                }}
            />
        </DialogComponentWrapper>


        {!acceso_limitado && canEdit && <DivPortal container="usersTableToolbar">
            <FontAwesomeIcon className='btnEdit pointer text-dark' size="lg" icon={faPlus}
                onClick={(event) => {
                    event.preventDefault();
                    if (!promiseWrapper2.current) return;
                    const promise = users.length >= 4 ? promiseWrapper2.current.getPromiseAnswer() : Promise.resolve(true);
                    promise.then((ok) => {
                        if (!ok) return;
                        setDialogState({ visible: true, headerText: t("users.add") });
                    });
                }}
            />
        </DivPortal>}

        {dataSource.length ?
            <>
                <DataTablePrime
                    tableName="usersTable"
                    t={t}
                    editableRow={!acceso_limitado && canEdit}
                    editFunction= {async (event, rowData) => {
                        if (!rowData || !rowData.permiso_id || !rowData.email) return;
                        setDialogState({
                            data: {
                                email: rowData.email, nombre: rowData.nombre, apellido_paterno: rowData.apellido_paterno, apellido_materno: rowData.apellido_materno, tipousuario_id: rowData.tipousuario_id, direccion: rowData.direccion,
                                ubicacion: rowData.ubicacion, telofono_fijo: rowData.telefono_fijo, telefono_movil: rowData.telefono_movil, pais: rowData.pais
                            }, visible: true, headerText: t("forms.edit"), existing_id: rowData.permiso_id
                        });
                    }}
                    deletableRow={!acceso_limitado && canEdit}
                    deleteFunction= {async (event, rowData) => {
                        if (!rowData || !rowData.permiso_id || !rowData.email) return;

                        if (!promiseWrapper.current) return;
                        promiseWrapper.current.getPromiseAnswer().then((del) => {
                            if (!del) return;
                            deleteDataAWS({ email: rowData.email },
                                "email", rowData.email, `/users/${farmId}/permission/${rowData.permiso_id}/delete`, t, addRecordFallback, updatingRef
                            );
                        });
                    }}
                    rowDataBoundFunction= {(rowData) => {
                        if (rowData && rowData.is_farm && rowData.permiso_admin && !acceso_limitado) return true;
                        else return false;
                    }}
                    items={dataSource}
                    size="small"
                    fields={[
                        {field:"permiso_id",header:names[0], hidden:true, bodyStyle:{textAlign:"center"}},
                        {field:"nombre_completo",body:templateRedirectToUser, header:names[1], bodyStyle:{textAlign:"center"}},
                        {field:"email",header:names[2], bodyStyle:{textAlign:"center"}, body:emailTemplate},
                        {field:"tipousuario",body:tipoUsuarioTemplate,header:names[3], bodyStyle:{textAlign:"center"}},
                    ]}
                />
            </>
            : <span className='no-data'>{t("forms.noData")}</span>}
    </>);
};
const RecordsUsersTable = ({ usersData, farmId, t, lng }) => {
    const grid = useRef(null);
    const names = useMemo(() => ["ID", t("general.name"), "DropControl", "DropControl APP", t("forms.riegosProgramados"), t("forms.comentariosRealizados")], [lng]);

    const dataSource = useMemo(() => usersData, [usersData, farmId]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    useColsEffect(grid, names);
    const gridRef = useGridRef(grid, dataSource);

    const { templateDaysDC, templateDaysDCAPP } = useMemo(() => {
        const templateDaysDC = ({ dias_dc }) => {
            if (!dias_dc) return 0;
            return (
                <a>
                    {dias_dc} {t("forms.dias")}
                </a>
            );
        };
        const templateDaysDCAPP = ({ dias_dc_mobile }) => {
            if (!dias_dc_mobile) return 0;
            return (
                <a>
                    {dias_dc_mobile} {t("forms.dias")}
                </a>
            );
        };

        return { templateDaysDC, templateDaysDCAPP };
    }, [lng]);

    if (!Array.isArray(dataSource)) return null;
    return (
        <>
            {dataSource.length ?
                <>
                    <DataTablePrime
                        selectionMode="single"
                        tableName="usersRegistersTable"
                        t={t}
                        selection={selectedUsers}
                        onSelectionChange={(e) => setSelectedUsers(e.value)}
                        items={dataSource}
                        size="small"
                        fields={[
                            {field:"permiso_id",header:names[0], hidden:true, bodyStyle:{textAlign:"center"}},
                            {field:"usuario", header:names[1], bodyStyle:{textAlign:"center"}},
                            {field:"dias_dc", body:templateDaysDC, header:names[2], bodyStyle:{textAlign:"center"}},
                            {field:"dias_dc_mobile",body:templateDaysDCAPP, header:names[3], bodyStyle:{textAlign:"center"}},
                            {field:"cant_riegos", header:names[4], bodyStyle:{textAlign:"center"}},
                            {field:"cant_comentarios", header:names[5], bodyStyle:{textAlign:"center"}},
                        ]}
                    />
                </>
                : <span className='no-data'>{t("forms.noData")}</span>}
        </>);
};

export { UsersTable, RecordsUsersTable };
